import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './AdminPage.module.css'; // Importación como módulo CSS
import logo from './logo2.png'; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faUsers, faUserTie, faChartBar, faMapMarkedAlt, faDollarSign, faUser, faLock, faEllipsisV } from '@fortawesome/free-solid-svg-icons'; 
import Notifications from './Notifications';
import Affiliates from './Affiliates'; 
import SectorManagers from './SectorManagers'; 
import Reports from './Reports'; 
import Sectors from './Sectors'; 
import Cobrar from './Cobrar'; 
import Users from './Users';
import ChangePassword from './ChangePassword';
import { hasPermission, PERMISSIONS, getUserData } from './permissions';
import { api } from './api';

const AdminPage = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentScreen, setCurrentScreen] = useState('');
  const [userData, setUserData] = useState(null);
  const [totalAfiliados, setTotalAfiliados] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  useEffect(() => {
    const user = getUserData();
    setUserData(user);
    
    // Obtener el conteo de afiliados
    const fetchAfiliados = async () => {
      try {
        const afiliadosData = await api.getAfiliados();
        setTotalAfiliados(afiliadosData.length);
      } catch (error) {
        console.error('Error al obtener afiliados:', error);
      }
    };
    
    fetchAfiliados();
    
    // Prevenir el acceso al presionar la flecha atrás del navegador
    window.history.pushState(null, null, window.location.href);
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('popstate', () => {
      window.history.pushState(null, null, window.location.href);
    });
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Cerrar el menú móvil cuando se hace click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('[data-mobile-menu]')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    if (window.confirm('¿Está seguro que desea cerrar sesión?')) {
      // Limpiar todo del localStorage y sessionStorage
      localStorage.clear();
      sessionStorage.clear();
      
      // Limpiar el historial para que no pueda volver atrás
      window.history.go(-window.history.length);
      
      // Redirigir al login usando React Router
      navigate('/login', { replace: true });
    }
  };



  const renderContent = () => {
    switch (currentScreen) {
      case 'notificaciones':
        return <Notifications />;
      case 'afiliados':
        return <Affiliates />;
      case 'encargados':
        return <SectorManagers />;
      case 'reportes':
        return <Reports />;
      case 'sectores':
        return <Sectors />;
      case 'cobrar':
        return <Cobrar />;
      case 'usuarios':
        return <Users />;
      case 'cambiar-contrasena':
        return <ChangePassword />;
      default:
        return <div><h2>Selecciona una opción</h2></div>;
    }
  };

  const handleButtonClick = (screen) => {
    setCurrentScreen(screen);
  };

  const handleBackClick = () => {
    setCurrentScreen('');
  };

  if (!userData) {
    return <div style={{ padding: '20px' }}>Cargando...</div>;
  }

  // Función para renderizar botones con validación de permisos
  const renderButton = (permission, screen, label, icon, isLast = false) => {
    const hasAccess = hasPermission(userData.rol, permission);
    const isLogout = screen === 'logout';
    const buttonClass = isLogout ? styles.logoutButton : `${styles.sidebarButton} ${isLast ? styles.sidebarButtonLast : ''}`;
    
    return (
      <button 
        key={screen}
        className={buttonClass}
        onClick={() => hasAccess && (isLogout ? handleLogout() : handleButtonClick(screen))}
        disabled={!hasAccess}
        title={!hasAccess ? 'Sin permisos para acceder' : ''}
        style={!hasAccess ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
      >
        <FontAwesomeIcon icon={icon} /> {label}
      </button>
    );
  };

  return (
    <div className={styles.adminPageContainer}>
      {/* Encabezado superior */}
      <div className={styles.topHeader}>
        <div className={styles.headerContent}>
          <img src={logo} alt="Logo" className={styles.headerLogo} />
          <div className={styles.headerRight}>
            {!isMobile && (
              <>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                  <FontAwesomeIcon icon={faUsers} style={{ fontSize: '16px', color: '#ffffff' }} />
                  <span style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', letterSpacing: '0.3px' }}>
                    {totalAfiliados}
                  </span>
                </div>
                <div className={styles.userInfo}>
                  <div className={styles.userNameContainer}>
                    <FontAwesomeIcon icon={faUser} className={styles.userIcon} />
                    <div>
                      <p className={styles.userName}>{userData?.nombre || userData?.username || userData?.email || 'Usuario'}</p>
                      <p className={styles.userRole}>{userData?.rol?.toUpperCase?.() || 'USUARIO'}</p>
                    </div>
                  </div>
                </div>
                <button className={styles.headerLogoutButton} onClick={handleLogout} title="Cerrar sesión">
                  <FontAwesomeIcon icon={faLock} /> <span className={styles.logoutButtonText}>Salir</span>
                </button>
              </>
            )}
            {isMobile && (
              <div style={{ position: 'relative' }} data-mobile-menu>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: '#ffffff',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    hover: { background: 'rgba(255, 255, 255, 0.3)' }
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </button>
                {mobileMenuOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    background: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    zIndex: 1000,
                    minWidth: '220px',
                    marginTop: '8px'
                  }}>
                    <div style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid #eee',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <FontAwesomeIcon icon={faUser} style={{ color: '#003366', fontSize: '16px' }} />
                      <div>
                        <p style={{ margin: '0', fontSize: '13px', fontWeight: '600', color: '#003366' }}>
                          {userData?.nombre || userData?.username || userData?.email || 'Usuario'}
                        </p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#666', textTransform: 'uppercase' }}>
                          {userData?.rol || 'Usuario'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        handleLogout();
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        background: 'none',
                        color: '#dc3545',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        textTransform: 'uppercase'
                      }}
                      onMouseEnter={(e) => e.target.style.background = 'rgba(220, 53, 69, 0.1)'}
                      onMouseLeave={(e) => e.target.style.background = 'none'}
                    >
                      <FontAwesomeIcon icon={faLock} style={{ fontSize: '14px' }} />
                      Salir
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    <div className={styles.container}>
      {isMobile && currentScreen ? (
        <>
          {renderContent()}
        </>
      ) : (
        <>
            {/* Barra lateral */}
            <div className={styles.sidebar}>
              <div className={styles.sidebarContent}>
                <div className={styles.buttonsContainer}>
                  {userData?.rol === 'encargado_de_sector' ? (
                    <>
                      {renderButton(PERMISSIONS.VIEW_NOTIFICATIONS, 'notificaciones', 'ATRASOS', faBell)}
                      {renderButton(PERMISSIONS.VIEW_SECTOR_AFFILIATES, 'afiliados', 'AFILIADOS', faUsers)}
                      {renderButton(PERMISSIONS.VIEW_SECTOR_REPORTS, 'reportes', 'REPORTES DE COBROS', faChartBar)}
                      {renderButton(PERMISSIONS.CHARGE_OWN_SECTOR, 'cobrar', 'COBRAR', faDollarSign)}
                      {renderButton(PERMISSIONS.CHANGE_PASSWORD, 'cambiar-contrasena', 'CAMBIAR CONTRASEÑA', faLock, true)}
                    </>
                  ) : (
                    <>
                      {renderButton(PERMISSIONS.VIEW_NOTIFICATIONS, 'notificaciones', 'ATRASOS', faBell)}
                      {renderButton(PERMISSIONS.VIEW_ALL_AFFILIATES, 'afiliados', 'AFILIADOS', faUsers)}
                      {renderButton(PERMISSIONS.EDIT_USER, 'encargados', 'ENCARGADOS', faUserTie)}
                      {renderButton(PERMISSIONS.VIEW_ALL_REPORTS, 'reportes', 'REPORTES DE COBROS', faChartBar)}
                      {renderButton(PERMISSIONS.DELETE_SECTOR, 'sectores', 'SECTORES', faMapMarkedAlt)}
                      {renderButton(PERMISSIONS.CHARGE_ANY_SECTOR, 'cobrar', 'COBRAR', faDollarSign)}
                      {renderButton(PERMISSIONS.EDIT_USER, 'usuarios', 'USUARIOS', faUser)}
                      {renderButton(PERMISSIONS.CHANGE_PASSWORD, 'cambiar-contrasena', 'CAMBIAR CONTRASEÑA', faLock, true)}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Pantalla de contenido según la selección */}
            <div className={styles.mainContent}>
              {renderContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
